// Extracts an initial cabinet interior layout from the actual cabinet GLB.
//
// The parser only treats semantic section nodes as layout sections:
//   Shelf###      -> shelf section
//   Drawer###     -> drawer section
//   Door Left### / Door_Left###   -> left half of a cabinet door-pair section
//   Door Right### / Door_Right### -> right half of a cabinet door-pair section
//
// Child meshes inherit the nearest semantic parent type, so meshes like
// Drawer_box, Drawer_Cap, Door_Left_Cap, or future handle-position meshes are
// included in their parent section bounds when they are nested correctly.
// Standalone helper/marker meshes are intentionally ignored; they should not
// create extra UI sections.

import { hasInvisibleObjectName } from '../BaseModelLoader';

const RAW_TYPE_TO_SECTION_TYPE = {
  shelf: 'shelf',
  drawer: 'drawer',
  door: 'cabinet',
};

function classifyObjectName(name) {
  if (/^Shelf\d*$/.test(name)) {
    return 'shelf';
  }

  if (/^Drawer\d+$/.test(name)) {
    return 'drawer';
  }

  if (/^Door[ _]Left(?:\d*|\.\d+)?$/.test(name)) {
    return 'door_left';
  }

  if (/^Door[ _]Right(?:\d*|\.\d+)?$/.test(name)) {
    return 'door_right';
  }

  return null;
}

function loadGlbScene(url) {
  return new Promise((resolve, reject) => {
    if (!window.GLTFLoader) {
      reject(new Error('[parseCabinetSections] GLTFLoader not available on window'));
      return;
    }

    const loader = new window.GLTFLoader();
    loader.load(url, (gltf) => resolve(gltf.scene), undefined, reject);
  });
}

function classifySectionObject(object) {
  let current = object;

  while (current && current.isObject3D) {
    const rawType = classifyObjectName(current.name || '');
    if (rawType) {
      return rawType;
    }

    current = current.parent;
  }

  return null;
}

function collectSectionMeshes(scene) {
  const THREE = window.THREE;
  const meshes = [];

  scene.traverse((child) => {
    if (!child.isMesh || hasInvisibleObjectName(child, scene)) {
      return;
    }

    const rawType = classifySectionObject(child);
    if (!rawType) {
      return;
    }

    const box = new THREE.Box3().setFromObject(child);
    meshes.push({
      name: child.name,
      rawType,
      centerY: (box.min.y + box.max.y) / 2,
      minY: box.min.y,
      maxY: box.max.y,
    });
  });

  return meshes;
}

function getMeshHeight(mesh) {
  return mesh.maxY - mesh.minY;
}

function hasStrongVerticalOverlap(first, second) {
  const overlap = getVerticalOverlap(first, second);
  const minHeight = Math.min(getMeshHeight(first), getMeshHeight(second));

  return minHeight > 0 && overlap / minHeight > 0.5;
}

function mergeSectionGroup(group) {
  return {
    rawType: group[0].rawType,
    centerY: group.reduce((sum, mesh) => sum + mesh.centerY, 0) / group.length,
    minY: Math.min(...group.map((mesh) => mesh.minY)),
    maxY: Math.max(...group.map((mesh) => mesh.maxY)),
  };
}

function mergeOverlappingSectionMeshes(meshes) {
  const groups = [];

  meshes.forEach((mesh) => {
    const existingGroup = groups.find((group) => (
      group[0].rawType === mesh.rawType
      && group.some((groupMesh) => hasStrongVerticalOverlap(groupMesh, mesh))
    ));

    if (existingGroup) {
      existingGroup.push(mesh);
      return;
    }

    groups.push([mesh]);
  });

  return groups.map(mergeSectionGroup);
}

function mergeDoorPairs(meshes) {
  const doorLefts = meshes.filter((mesh) => mesh.rawType === 'door_left');
  const doorRights = meshes.filter((mesh) => mesh.rawType === 'door_right');
  const others = meshes.filter((mesh) => mesh.rawType !== 'door_left' && mesh.rawType !== 'door_right');

  const usedRights = new Set();
  const doors = [];

  doorLefts.forEach((left) => {
    let best = null;
    let bestDist = Infinity;

    doorRights.forEach((right) => {
      if (usedRights.has(right.name)) {
        return;
      }

      const dist = Math.abs(left.centerY - right.centerY);
      if (dist < bestDist) {
        bestDist = dist;
        best = right;
      }
    });

    if (best) {
      usedRights.add(best.name);
      doors.push({
        rawType: 'door',
        centerY: (left.centerY + best.centerY) / 2,
        minY: Math.min(left.minY, best.minY),
        maxY: Math.max(left.maxY, best.maxY),
      });
      return;
    }

    doors.push({ ...left, rawType: 'door' });
  });

  doorRights.forEach((right) => {
    if (!usedRights.has(right.name)) {
      doors.push({ ...right, rawType: 'door' });
    }
  });

  return mergeOverlappingSectionMeshes([...others, ...doors]);
}

function getSectionBounds(meshes) {
  if (meshes.length === 0) {
    return null;
  }

  return {
    minY: Math.min(...meshes.map((mesh) => mesh.minY)),
    maxY: Math.max(...meshes.map((mesh) => mesh.maxY)),
  };
}

function getVerticalOverlap(first, second) {
  return Math.min(first.maxY, second.maxY) - Math.max(first.minY, second.minY);
}

function isShelfInsideCabinet(shelf, cabinet) {
  if (shelf.centerY < cabinet.minY || shelf.centerY > cabinet.maxY) {
    return false;
  }

  return getVerticalOverlap(shelf, cabinet) > 0;
}

function mergeInteriorShelvesIntoCabinets(meshes) {
  const cabinets = meshes
    .filter((mesh) => mesh.rawType === 'door')
    .map((cabinet) => ({ ...cabinet, shelfCount: 0 }));
  const remainingMeshes = meshes.filter((mesh) => mesh.rawType !== 'door' && mesh.rawType !== 'shelf');

  meshes
    .filter((mesh) => mesh.rawType === 'shelf')
    .forEach((shelf) => {
      const parentCabinet = cabinets
        .filter((cabinet) => isShelfInsideCabinet(shelf, cabinet))
        .sort((a, b) => getVerticalOverlap(shelf, b) - getVerticalOverlap(shelf, a))[0];

      if (parentCabinet) {
        parentCabinet.shelfCount += 1;
        return;
      }

      remainingMeshes.push(shelf);
    });

  return [...remainingMeshes, ...cabinets];
}

function buildSectionsWithRatios(mergedMeshes, totalMinY, totalMaxY) {
  const totalHeight = totalMaxY - totalMinY;
  if (totalHeight <= 0 || mergedMeshes.length === 0) {
    return [];
  }

  const sectionMeshes = mergeInteriorShelvesIntoCabinets(mergedMeshes);
  const sorted = [...sectionMeshes].sort((a, b) => b.centerY - a.centerY);

  return sorted.map((mesh, index) => {
    let top = mesh.maxY;
    let bottom = mesh.minY;

    if (mesh.rawType === 'shelf') {
      const previousMesh = sorted[index - 1];
      const nextMesh = sorted[index + 1];

      top = previousMesh ? previousMesh.minY : top;
      bottom = nextMesh ? nextMesh.maxY : bottom;
    }

    return {
      type: RAW_TYPE_TO_SECTION_TYPE[mesh.rawType] || 'cabinet',
      heightRatio: Math.max(0, top - bottom) / totalHeight,
      quantity: mesh.rawType === 'door' ? mesh.shelfCount : undefined,
    };
  });
}

function disposeScene(scene) {
  scene.traverse((child) => {
    if (!child.isMesh) {
      return;
    }

    child.geometry.dispose();
    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material.dispose());
    } else if (child.material) {
      child.material.dispose();
    }
  });
}

const cacheByUrl = new Map();

/**
 * Parses section ratios from a cabinet model URL.
 * Returns null if model loading or mesh-name parsing fails.
 */
export async function getCabinetSectionsFromModel(url) {
  if (!url) {
    return null;
  }

  if (cacheByUrl.has(url)) {
    return cacheByUrl.get(url);
  }

  try {
    const scene = await loadGlbScene(url);
    const raw = collectSectionMeshes(scene);
    const merged = mergeDoorPairs(raw);
    const sectionBounds = getSectionBounds(merged);

    if (!sectionBounds) {
      disposeScene(scene);
      cacheByUrl.set(url, null);
      return null;
    }

    const sections = buildSectionsWithRatios(merged, sectionBounds.minY, sectionBounds.maxY);
    disposeScene(scene);

    const parsedSections = sections.length ? sections : null;
    cacheByUrl.set(url, parsedSections);
    return parsedSections;
  } catch (error) {
    console.warn('[cabinetSectionsParser] Could not parse cabinet model sections:', error.message);
    cacheByUrl.set(url, null);
    return null;
  }
}
